import { useLazyQuery, useMutation } from '@apollo/client';
import {
  Alert,
  AlertIcon,
  AlertTitle,
  Box,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import Spinner from '@components/icons/Spinner';
import Button from '@components/interactive/Button';
import Header from '@components/typography/Header';
import GET_DASHBOARD_CATALOG from 'gql/GetDashboardCatalog';
import SUBMIT_USER_BENEFIT_REQUEST from 'gql/SubmitUserBenefitRequest';
import { useRouter } from 'next/router';
import { useEffect, useReducer } from 'react';
import { PERIOD_STRINGS } from '../dashboard/benefits/getBenefitContent';
import { DashboardHelp } from '../dashboard/DashboardHelp';
import { DashboardBenefit, UserBenefitRequestStatus } from '../dashboard/types';
import Basket from './Basket';
import Controls from './Controls';
import ItemGrid from './ItemGrid';
import reducer, { initialState } from './reducer';
import { ActionType, DashboardOrder } from './types';

const Catalog = ({ userData, handleLogout }) => {
  const router = useRouter();
  const [state, dispatch] = useReducer(reducer, initialState);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [getDashboardCatalog, { loading }] = useLazyQuery(GET_DASHBOARD_CATALOG, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      dispatch({
        type: ActionType.SET_INITIAL_DATA,
        value: data.getDashboardCatalog,
      });
    },
    onError: () => {
      router.push('/account/dashboard');
    },
  });

  const [submitUserBenefitRequest] = useMutation(SUBMIT_USER_BENEFIT_REQUEST, {
    onCompleted: (data) => {
      router.prefetch('/account/dashboard');
    },
  });

  useEffect(() => {
    if (!router.query.id) {
      router.push('/account/dashboard');
    }

    getDashboardCatalog({
      variables: {
        id: userData.id,
        benefitId: router.query.id,
      },
    });
  }, [router.query]);

  const onSearch = (e: any) => {
    dispatch({
      type: ActionType.SEARCH_ITEMS,
      value: {
        searchInput: e.target.value,
      },
    });
  };

  const onFilter = (index: number) => {
    dispatch({
      type: ActionType.FILTER_ITEMS,
      value: { index },
    });
  };

  const submitOrder = (userId: string, benefit: DashboardBenefit, order: DashboardOrder) => {
    submitUserBenefitRequest({
      variables: {
        user_id: userId,
        status: UserBenefitRequestStatus.SUBMITTED,
        benefit_type: 'OTC',
        benefit_id: benefit.id,
        benefit_info: benefit.benefit_info,
        order,
      },
    });
  };

  const onItemClick = (event: any) => {
    const itemId = event.target.getAttribute('data-item');

    dispatch({ type: ActionType.INCREMENT_ITEM, value: { itemId } });
  };

  const removeItem = (index: string) => {
    dispatch({ type: ActionType.REMOVE_ITEM, value: { itemId: index } });
  };

  const remainingAmount = (state.order && state.order.remainingAmount) || 0;
  const maxAmount = (state.order && state.order.maxAmount) || 0;
  const benefitInfo = state.benefit && state.benefit.benefit_info;

  return (
    <Stack mx={2} w="100%">
      <Header as="h2">Review Order</Header>
      {loading ? (
        <Flex>
          <Spinner />
        </Flex>
      ) : (
        <Box>
          <Heading mb={5} as="h1">
            Claim your over-the-counter credit
          </Heading>
          <Text mb={10} fontSize="lg">
            {`Your plan offers a $${benefitInfo && benefitInfo.otc_benefit_max_amnt} ${
              PERIOD_STRINGS[benefitInfo && benefitInfo.otc_benefit_max_per]
            } credit for over-the-counter items. Choose the items you’d like below, we’ll place the order
        for you, and you’ll receive them within a month. This service and the
        items (as long as their cost is below the amount of your monthly credit)
        is entirely free.`}
          </Text>
          <Flex direction={{ base: 'column', md: 'row' }} mb={5}>
            <Box>
              <Heading size="md">Available Credit</Heading>
              <Text
                id="amount_remaining"
                mt={5}
                fontSize={40}
                color={remainingAmount < 0 ? 'red.500' : 'black'}
                fontWeight={800}
              >
                {remainingAmount < 0 ? `-$${Math.abs(remainingAmount)}` : `$${remainingAmount}`}
              </Text>
              <Text fontSize="lg">{`out of $${maxAmount}`}</Text>
            </Box>
            <Spacer />
            <Basket basketItems={state.order?.items} onDelete={removeItem} />
            <Spacer />
            <Flex mt={{ base: 10, md: 0 }} alignItems="center">
              <Button
                id="submit_order"
                isDisabled={!state.order?.total || !state.order.valid}
                onClick={() => {
                  const order = {
                    total: state.order.total,
                    items: state.order.items,
                  };
                  submitOrder(userData.id, state.benefit, order);
                  window.sessionStorage.removeItem('order');
                  onOpen();
                }}
              >
                Place Order
              </Button>
            </Flex>
          </Flex>
          {state.order && !state.order.valid && (
            <Alert mb={10} status="error">
              <AlertIcon />
              <AlertTitle>Your order amount is greater than your benefit amount.</AlertTitle>
            </Alert>
          )}
          <Controls selectedFilterIndex={state.selectedFilterIndex} onFilter={onFilter} onSearch={onSearch} />
          <ItemGrid items={state.items} order={state.order} onItemClick={onItemClick} />
          <DashboardHelp handleLogout={handleLogout} />
        </Box>
      )}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Order Submitted! 🎉</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="lg">You will receive a confirmation email shortly</Text>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              mr={3}
              onClick={() => {
                /* Next JS Link prefetching isn't working properly for how
                 * we fetch the user's dashboard. For now, let's do
                 * a complete page refresh so we get the latest user data
                 * in the dashboard.
                 */
                window.location.href = '/account/dashboard';
              }}
            >
              Go to dashboard
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Stack>
  );
};

export default Catalog;
